import { useEffect } from 'react'
import { isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router'
import styled from 'styled-components'
import AreaTitle from '@/components/area-title'
import { Button } from '@/components/button'
import Panel from '@/components/panel'
import Skeleton from '@/components/skeleton'
import Page from '@/components/page'
import useCurrentSession from '@/hooks/use-current-session'
import { showBillingInfoModal } from '@/features/Settings/PlanSettings/billing-info-modal'
import { breakpoints } from '@/styles'
import { useFlag } from '@/flags'
import PaymentSummary from './payment-summary'
import { PaymentMethod } from './use-payment-methods'
import { parsePlan } from './use-plan'

const StyledPage = styled(Page)`
  height: 100%;
`

const ContentWrapper = styled.div`
  position: relative;
  max-width: 716px;
  width: 100%;
  margin: auto;
  flex: 1;
`

const StyledPanel = styled(Panel)`
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 16px 24px;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    align-items: flex-start;
    padding: 24px;
    > div,
    > button {
      width: 100%;
    }
  }
`

type PlanSettingsPaymentProps = RouteComponentProps<Nothing> & {
  card?: PaymentMethod
  isLoading: boolean
}

const PlanSettingsPaymentView: React.FC<PlanSettingsPaymentProps> = ({
  isLoading,
  card,
  history
}) => {
  const taxFlag = useFlag(['features', 'pricing', 'taxCode'], false)
  const { t } = useTranslation()
  const session = useCurrentSession()
  const userRole = session?.user_role
  const basePlanName = session?.company_info?.base_plan_name
  const isAdmin = userRole === 'administrator'
  const [planType] = parsePlan(basePlanName)
  const isSpecialPlan = planType === 'special'
  const canEdit = isAdmin && !isSpecialPlan
  const noCard = !isLoading && isEmpty(card)
  useEffect(() => {
    if (!noCard && canEdit) return
    const { location } = history
    const query = location.search
    history.replace(`/settings/plan-settings${query}`)
  }, [noCard, history, canEdit])
  return (
    <StyledPage>
      <ContentWrapper>
        <AreaTitle>
          {t('l.plan_settings.view.payment_method.description', 'Payment Method')}
        </AreaTitle>
        <StyledPanel hSpace={0} vSpace={0}>
          <Row>
            <PaymentSummary card={card} showType isLoading={isLoading} />
            {isLoading ? (
              <Skeleton height={52} width={160} />
            ) : (
              <Button
                theme="secondary"
                onClick={() =>
                  showBillingInfoModal({
                    card,
                    taxFlag
                  })
                }
              >
                {t('common.edit', 'Edit')}
              </Button>
            )}
          </Row>
        </StyledPanel>
      </ContentWrapper>
    </StyledPage>
  )
}

export default PlanSettingsPaymentView
